import { Form, Formik, useFormikContext } from 'formik';
import { FC, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AutoExpandTextArea } from '../../../../components/AutoExpandTextArea';
import { FormField } from '../../../../components/FormField';
import {
  Input,
  InputWithContent,
} from '../../../../components/TextField/views';
import styles from './LogbookForm.module.css';
import {
  saveReportForLater,
  setLogbookFormState,
  setLogbookPage,
  useLogbookFormState,
} from '../../../../services/logbook';
import { ReportFooter } from '../views';
import { Button } from '../../../../components/Button';
import { LogbookFormState } from '../../../../types/logbook';
import { number, object, string } from 'yup';

const validationSchema = object({
  vehicleName: string().required('RequiredField'),
  endKm: number()
    .required('RequiredField')
    .test(
      'startEnd',
      'Logbook.StartBiggerThanEnd',
      (value, ctx) => value >= ctx.parent.startKm
    ),
  startKm: number()
    .required()
    .test(
      'startEnd',
      'Logbook.StartBiggerThanEnd',
      (value, ctx) => value <= ctx.parent.endKm
    ),
  startAddress: string().required('RequiredField'),
  endAddress: string().required('RequiredField'),
  notes: string(),
});

export const LogbookFormPage: FC = () => {
  const state = useLogbookFormState();

  return (
    <Formik
      initialValues={state}
      onSubmit={(state) => {
        setLogbookFormState(state);
        setLogbookPage(3);
      }}
      validationSchema={validationSchema}
    >
      <LogbookForm />
    </Formik>
  );
};

export const LogbookForm: FC = () => {
  const { values, submitForm } = useFormikContext<LogbookFormState>();
  const { $t } = useIntl();

  useEffect(() => {
    /** sync formik with rxjs state so that when user is saving for later
     * we can still save the form even if it's invalid
     */
    setLogbookFormState(values);
  }, [values]);

  return (
    <>
      <Form className={styles.form}>
        <FormField<typeof Input>
          component={Input}
          id="driverName"
          name="driverName"
          label={$t({ id: 'Logbook.NameSurename' })}
          disabled
        />
        <FormField<typeof Input>
          component={Input}
          id="vehicleName"
          name="vehicleName"
          label={$t({ id: 'Logbook.Reg' })}
        />
        <FormField<typeof Input>
          component={Input}
          id="day"
          name="day"
          label={$t({ id: 'Logbook.Date' })}
          disabled
        />
        <div className={styles.formGroup}>
          <FormField<typeof InputWithContent>
            component={InputWithContent}
            id="startKm"
            name="startKm"
            label={$t({ id: 'Logbook.KmStart' })}
            type="number"
          >
            km
          </FormField>
          <FormField<typeof InputWithContent>
            component={InputWithContent}
            id="endKm"
            name="endKm"
            label={$t({ id: 'Logbook.KmEnd' })}
            type="number"
          >
            km
          </FormField>
        </div>
        <div className={styles.prefilled}>
          <FormattedMessage id="Total" />
          <div>{values.endKm - values.startKm} km</div>
        </div>
        <FormField<typeof Input>
          component={Input}
          id="startAddress"
          name="startAddress"
          label={$t({ id: 'Logbook.StartingPoint' })}
        />
        <FormField<typeof Input>
          component={Input}
          id="endAddress"
          name="endAddress"
          label={$t({ id: 'Logbook.EndingPoint' })}
        />

        <FormField<typeof AutoExpandTextArea>
          component={AutoExpandTextArea}
          label={$t({ id: 'Logbook.Notes' })}
          className={styles.textarea}
          placeholder={`${$t({
            id: 'Logbook.NoNotes',
          })}...`}
          name="notes"
          maxLength={250}
        />
      </Form>
      <ReportFooter>
        <Button grow onClick={submitForm}>
          <FormattedMessage id="NextStep" />
        </Button>
        <Button
          variant="text"
          grow
          onClick={() => {
            saveReportForLater();
          }}
        >
          <FormattedMessage id="SaveForLater" />
        </Button>
        <Button variant="text" grow onClick={() => setLogbookPage(1)}>
          <FormattedMessage id="Back" />
        </Button>
      </ReportFooter>
    </>
  );
};
